import { useState, useEffect, useMemo, memo, useCallback, useRef, lazy } from 'react';
import useCampaignData from '../../data/useCampaignData';
import useProfile from '../../data/useProfile';
import stepLancher from '../../utils/templateBuilder';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { appSlice, useSelectorQueryApp } from '../../reducers/appReducer';
import useLocalStorage from '../../hooks/useLocaleStorage';
import initI18n from '../../i18n';
import daysjs from 'dayjs';
import * as isLeapYear from 'dayjs/plugin/isLeapYear';
import useStepsExplain from '../../hooks/useStepExplain';
import { checkIfSessionCouldBeStarted } from '../../utils/business';
import AuthError from '../shared/Error/AuthError';
import { bool, func } from 'prop-types';
import { errorPrivateNavigationFirefox, SECTIONS } from '../../utils/constants';
import Header from '../Header';
import useDiskStorage from '../../hooks/useDiskStorage';
import NavigatorNotCompatible from '../Technique/TestTechnique/Errors/NavigatorNotCompatible';
import AnalyticsProvider from './AnalyticsProvider';
import * as LDClient from 'launchdarkly-js-client-sdk';

const AppCandidate = lazy(() =>
	import(
		/* webpackChunkName: "AppCandidate" */
		'../EntryPoint/AppCandidate'
	)
);

export const InitApp = memo(() => {
	const { data, mutate } = useCampaignData();
	const dispatch = useDispatch();
	const { INIT_APP } = appSlice.actions;
	const { errorRef: errorStorage } = useDiskStorage({ mode: 'read' });
	const { data: profile } = useProfile();
	const [, setCandidateEmail, deleteCandidateEmail] = useLocalStorage('emailCandidate');
	const [, , deleteTestStarted] = useLocalStorage('testStarted', void 0, 'sessionStorage');
	const [localSurvey] = useLocalStorage('survey', null, 'sessionStorage');

	const [token] = useLocalStorage('localToken');
	const tokenRef = useRef(token);
	const testTechniqueOk = useSelectorQueryApp('testTechnique');
	const [allStepsInit, setAllSteps] = useState(null);
	const { status = 'invited' } = profile;
	const {
		product,
		hasVideoAnswers,
		globalTimer,
		requisites,
		landing,
		outro,
		survey,
		proctoring: { randomPictures },
		utils: { isSessionCampaign } = {},
	} = data;

	const [redirectToMaintenancePage, setRedirectToMaintenancePage] = useState(false);

	/**
	 *
	 * @type {({icon: {img: *, type: string}, video: boolean, proctoring: boolean, label: string, type: string, title: string, content: string}|{icon: {img: *, type: string}, video: boolean, label: string, type: string, title: string, content: [string, string]}|{icon: {img: *, type: string}, label: string, type: string, title: string, content: string|null}|{stepToCount: boolean, icon: {img: *, type: string}, label: string, type: string, title: string, content: [string, string, string]}|{stepToCount: boolean, icon: {img: *, type: string}, label: string, type: string, title: string, content: [string, string]})[]|unknown[]}
	 */
	const steps = useStepsExplain({
		globalTimer,
		hasVideoAnswers,
		product,
		hasProctoring: randomPictures,
		hasRequisites: requisites?.items?.length,
	});

	const { actualStepLoader, template } = useMemo(
		() =>
			stepLancher({
				hasVideoAnswers,
				hasProctoring: randomPictures,
				hasRequisites: requisites?.items?.length,
				testTechniqueOk,
				isSessionCampaign,
				outro,
				survey,
				alreadyOnSurveyStep: localSurvey,
				landing,
				needRequisite: profile.needRequisite,
			}),
		[
			localSurvey,
			survey,
			requisites?.items,
			testTechniqueOk,
			profile.needRequisite,
			hasVideoAnswers,
			isSessionCampaign,
			outro,
			landing,
			randomPictures,
		]
	);

	const allSteps = useMemo(() => template[status], [template, status]);

	const actualStep = useMemo(() => actualStepLoader[status], [actualStepLoader, status]);

	const launchApp = useCallback(() => {
		if (allSteps) {
			dispatch(
				INIT_APP({
					allSteps,
					actualStep,
					steps,
					product,
				})
			);
			setAllSteps(allSteps);
		}
	}, [INIT_APP, dispatch, allSteps, actualStep, steps, product]);

	const checkLaunchDarkly = useCallback(async () => {
		const LDUserName = `c_${profile.status === 'new' ? 'campaign' : 'candidate'}_token/${token}`;
		const user = { name: LDUserName, key: LDUserName };
		const client = LDClient.initialize(data.launchDarklyClientSideKey, user);

		try {
			await client.waitForInitialization();
			client.variation('exam-maintenance', true) && setRedirectToMaintenancePage(true);
		} catch (err) {
			setRedirectToMaintenancePage(true);
		}
	}, [data.launchDarklyClientSideKey, profile.status, token]);

	useEffect(() => {
		if (['new', 'joined', 'invited'].includes(status)) {
			(async () => {
				if (data.launchDarklyCheck) {
					await checkLaunchDarkly();
				}
			})();
		}
	}, [checkLaunchDarkly, data.launchDarklyCheck, status]);

	useEffect(() => {
		if (localSurvey && (!data.mobileMode || data.mobileURLRedirection)) {
			mutate(
				(campaignData) => ({
					...campaignData,
					mobileMode: true,
					mobileURLRedirection: '',
				}),
				false
			);
		}
	}, [localSurvey, mutate, data.mobileMode, data.mobileURLRedirection]);

	useEffect(() => {
		if (data.session && profile.status) {
			checkIfSessionCouldBeStarted(data.session, profile.expirationDate, profile.status);
		}
	}, [data.session, profile.expirationDate, profile.status]);

	useEffect(() => {
		if (profile.rescued || actualStep !== SECTIONS) {
			deleteTestStarted();
		}
	}, [profile.rescued, deleteTestStarted, actualStep]);

	useEffect(() => {
		if (profile.email) {
			// Removed Sentry's setUser usage, now using a custom logging method if needed
			console.log(`User data: email - ${profile.email}, id - ${profile.id}`);
		}
	}, [profile.email, profile.id]);

	useEffect(() => {
		// We set the email in localstorage to send it back when session expires. Useful for public campaign
		if (profile.status === 'invited' && profile.email) {
			deleteCandidateEmail();
		}
	}, [profile.email, profile.status, setCandidateEmail, deleteCandidateEmail]);

	useEffect(() => {
		if (product) {
			launchApp();
		}
	}, [product, launchApp]);

	if (errorStorage?.current?.message === errorPrivateNavigationFirefox) {
		return <NavigatorNotCompatible showButtonContactSupport={false} />;
	}

	// if many page are open, kill the previous ones
	if (tokenRef.current !== token) {
		return <Redirect to="/error-tab" />;
	}

	if (redirectToMaintenancePage) {
		return <Redirect to="/maintenance" />;
	}

	if (redirectToMaintenancePage) {
		return <Redirect to="/maintenance" />;
	}
	return allStepsInit ? (
		<AnalyticsProvider>
			<AppCandidate data={data} />
		</AnalyticsProvider>
	) : null;
});

const InitTrad = memo(({ usedSignin = false, setUsedSignin }) => {
	const { data } = useCampaignData();

	useEffect(() => {
		if (data.clientId && data.locale) {
			(async function initI18nDays() {
				if (!usedSignin) {
					setUsedSignin(true);
				}
				const [lng] = data.locale.split('-');

				await initI18n(data.clientId, data.locale);
				daysjs.extend(isLeapYear);
				await import(
					/* webpackChunkName: "datejs" */
					`dayjs/locale/${lng}.js`
				);
				daysjs.locale(lng);
			})();
		}
	}, [data.clientId, data.locale, setUsedSignin, usedSignin]);

	if (data.error === 401 || data === '') {
		return <AuthError />;
	}

	return (
		<>
			<Header />
			<InitApp data={data} />
		</>
	);
});

InitTrad.propTypes = {
	setUsedSignin: func,
	usedSignin: bool,
};

export default InitTrad;
